'use client'

import { useEffect, useState } from 'react'

interface ViewTrackerProps {
  postId: string
}

/**
 * Client-side view tracker component that sends view information to the API
 *
 * The server will extract various data points from the request:
 * - IP address
 * - User agent (for device detection)
 * - Referrer
 * - Geographic info if available
 *
 * This data is used to track both total views, unique views,
 * and detailed analytics about visitors.
 */
export const ViewTracker: React.FC<ViewTrackerProps> = ({ postId }) => {
  const [tracked, setTracked] = useState(false)

  useEffect(() => {
    // Skip tracking if already tracked or no postId
    if (tracked || !postId) return

    const trackView = async () => {
      try {
        // Delay tracking slightly to ensure it doesn't block page rendering
        await new Promise((resolve) => setTimeout(resolve, 1500))

        // Get the document referrer if available
        const referrer = document.referrer || undefined

        const response = await fetch('/api/track-view', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            postId,
            referrer,
          }),
        })

        if (response.ok) {
          setTracked(true)
        } else {
          console.error('Failed to track view')
        }
      } catch (error) {
        console.error('Error tracking view:', error)
      }
    }

    trackView()
  }, [postId, tracked])

  // This component doesn't render anything visually
  return null
}
